import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import * as transactionService from "@/shared/api/services/transactionService";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getMerchantName } from "@/shared/utils";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Calendar } from "@/shared/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Search, Calendar as CalendarIcon, Download, RefreshCw, ChevronLeft, ChevronRight, Globe, Filter, X } from "lucide-react";
import AdminTransactionTable from "@/shared/components/admin/AdminTransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";


export default function AdminGlobalTransactions() {
  const { t, language } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    engine: "all",
    reseller: "all",
    merchant: "all",
    provider: "all",
    status: "all",
    type: "all",
    currency: "all",
    location: "all",
    dateFrom: null,
    dateTo: null,
  });
  const [selectedTx, setSelectedTx] = useState(null);
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [merchantIds, setMerchantIds] = useState([]);
  const [search, setSearch] = useState("");
  const pageSize = 10;

  const { data: paginationResult, isLoading: isLoadingTransactions, refetch: refetchTransactions, error: transactionsError } = useQuery({
    queryKey: ["admin-global-transactions", page, pageSize, search, filters, merchantIds],
    queryFn: () => {
      const backendFilters = {};
      if (filters.status !== "all") backendFilters.status = filters.status;
      if (filters.type !== "all") backendFilters.type = filters.type;
      if (filters.provider !== "all") backendFilters.provider = filters.provider;
      if (filters.currency !== "all") backendFilters.currency = filters.currency;
      if (filters.location !== "all") backendFilters.location = filters.location;
      if (filters.dateFrom) backendFilters.startDate = filters.dateFrom.toISOString();
      if (filters.dateTo) {
        const endOfToDay = new Date(filters.dateTo);
        endOfToDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfToDay.toISOString();
      }
      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        search,
        backendFilters,
        merchantIds
      );
    },
    keepPreviousData: true,
  });

  const rawTransactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  const combinedData = React.useMemo(() => {
    return mapGlobalTransactions(rawTransactions);
  }, [rawTransactions]);

  const isLoading = isLoadingTransactions;
  const error = transactionsError;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchTransactions();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const { data: uniqueProviderNames = [] } = useQuery({
    queryKey: ["all-transaction-providers"],
    queryFn: () => transactionService.getUniqueProviders(),
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  useEffect(() => {
    const hasEntityFilters =
      filters.engine !== "all" ||
      filters.reseller !== "all" ||
      filters.merchant !== "all";

    if (!hasEntityFilters) {
      setMerchantIds([]);
      return;
    }

    const merchantIdSet = new Set();

    if (filters.engine !== "all") {
      const resellerIdsUnderEngine = resellers
        .filter((r) => r.createdByEngineId === filters.engine)
        .map((r) => r.id);

      merchants
        .filter((m) => m.createdByEngineId === filters.engine || resellerIdsUnderEngine.includes(m.createdByResellerId))
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.reseller !== "all") {
      merchants
        .filter((m) => m.createdByResellerId === filters.reseller)
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.merchant !== "all") {
      merchantIdSet.add(filters.merchant);
    }

    const nextMerchantIds = Array.from(merchantIdSet);
    const normalizedIds = nextMerchantIds.length > 0 ? nextMerchantIds : ["NO_MERCHANT_FOUND"];

    setMerchantIds((prev) => {
      if (prev.length === normalizedIds.length && prev.every((id, index) => id === normalizedIds[index])) {
        return prev;
      }
      return normalizedIds;
    });
  }, [filters.engine, filters.reseller, filters.merchant, merchants, resellers]);

  const providerOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    const addOption = (value, label, aliases = []) => {
      const normalizedValue = String(value || "").trim();
      if (!normalizedValue) return;
      if (seen.has(normalizedValue)) return;

      seen.add(normalizedValue);
      options.push({
        value: normalizedValue,
        label: label || normalizedValue,
        aliases: [normalizedValue, ...(aliases || [])].filter(Boolean),
      });
    };

    providers.forEach((p) => {
      const label = p.nameEn || p.name || p.code;
      addOption(p.id, label, [p.code, p.name, p.nameEn]);
    });

    combinedData.forEach((tx) => {
      const name = tx.client_bank_name || tx.bank_name || tx.provider_code || tx.bank_code;
      const provider = providers.find(p =>
        p.code === tx.provider_code ||
        p.name === name ||
        p.nameEn === name ||
        p.code === tx.bank_code
      );
      const value = provider ? provider.id : name;
      addOption(value, name, [tx.provider_code, tx.bank_code]);
    });

    uniqueProviderNames.forEach((entry) => {
      const name = typeof entry === "string"
        ? entry
        : (entry?.bankName || entry?.name || entry?.provider || entry?.value);

      const provider = providers.find(p =>
        p.name === name ||
        p.nameEn === name ||
        p.code === name
      );
      const value = provider ? provider.id : name;
      addOption(value, name);
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [providers, combinedData, uniqueProviderNames]);

  const locationOptions = [
    { value: "THB", label: "Thai" },
    { value: "IDR", label: "IDR" },
    { value: "PK", label: "Pakistan" },
  ];

  const resetFilters = () => {
    setFilters({
      engine: "all",
      reseller: "all",
      merchant: "all",
      provider: "all",
      status: "all",
      type: "all",
      currency: "all",
      location: "all",
      dateFrom: null,
      dateTo: null,
    });
    setMerchantIds([]);
    setSearch("");
    setPage(0);
  };

  const customDateRangeLabel = (filters.dateFrom || filters.dateTo)
    ? `${filters.dateFrom ? format(filters.dateFrom, "MMM d, yyyy") : "Start"} - ${filters.dateTo ? format(filters.dateTo, "MMM d, yyyy") : "End"}`
    : null;


  const handleExport = () => {
    const headers = [t("type"), t("paymentId"), t("reference"), t("merchant"), t("reseller"), t("provider"), t("amount"), t("status"), t("date")];
    const rows = combinedData.map((tx) => [
      tx.type === "WITHDRAWAL" ? t("withdrawal") : t("deposit"),
      tx.transaction_id || tx.payment_id || tx.id,
      tx.reference || (tx.type === "WITHDRAWAL" ? tx.bank_account || "" : "") || "",
      tx.merchant?.name || tx.merchant?.code || (tx.merchant_id ? (merchants.find((m) => m.id === tx.merchant_id)?.name || "") : "") || "",
      tx.reseller?.name || (tx.merchant?.createdByResellerId ? (resellers.find((r) => r.id === tx.merchant.createdByResellerId)?.name || "") : "") || "Direct",
      tx.client_bank_name || tx.bank_name || "",
      (tx.ammount ?? tx.amount ?? 0).toString(),
      tx.status,
      tx.created_date || tx.createdAt,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `global-transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const handleEntityFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
    setPage(0);
  };


  const selectedMerchant = selectedTx
    ? (selectedTx.merchant?.id ? selectedTx.merchant : (selectedTx.merchant_id ? merchants.find((m) => m.id === selectedTx.merchant_id) : null))
    : null;
  const selectedReseller = selectedTx
    ? (selectedTx.reseller?.id
      ? selectedTx.reseller
      : (selectedTx.reseller_id
        ? (resellers.find((r) => r.id === selectedTx.reseller_id) || { id: selectedTx.reseller_id, name: selectedTx.reseller?.name || selectedTx.reseller_name })
        : (selectedMerchant?.createdByResellerId
          ? resellers.find((r) => r.id === selectedMerchant.createdByResellerId)
          : null)))
    : null;
  const selectedEngine = null;

  const activeFiltersCount =
    Object.entries(filters).filter(([k, v]) => v && v !== "all" && k !== "search").length +
    (search ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">{t("errorLoadingTransactions")}</p>
          <p className="text-red-600 text-sm mt-1">{error.message || t("pleaseCheckConnection")}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Globe className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("globalTransactions")}</h1>
            <p className="text-slate-500">
              {isLoading ? (
                <Skeleton width={250} />
              ) : (
                `${totalTransactions.toLocaleString()} ${t("transactionsAcrossEntities")}`
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {t("refresh")}
          </Button>
          <Button variant="outline" onClick={handleExport} className="gap-2">
            <Download className="h-4 w-4" />
            {t("export")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="font-medium text-slate-700">{t("filters")}</span>
            {activeFiltersCount > 0 && (
              <Badge className="bg-blue-100 text-blue-700">{activeFiltersCount} {t("activeFilters")}</Badge>
            )}
          </div>
          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1 text-slate-500">
              <X className="h-4 w-4" />
              {t("clearAll")}
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {/* First row: Search and main filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("searchIdRefMerchant")}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-10"
              />
            </div>

            {/* Type Filter */}
            <Select value={filters.type} onValueChange={(v) => { setFilters({ ...filters, type: v }); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder={t("allTypes") || "All types"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allTypes") || "All types"}</SelectItem>
                <SelectItem value="deposit" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("deposit")}</SelectItem>
                <SelectItem value="withdrawal" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("withdrawal")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Currency Filter */}
            <Select value={filters.currency} onValueChange={(v) => { setFilters({ ...filters, currency: v }); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder={t("allCurrencies") || "All Currencies"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allCurrencies") || "All Currencies"}</SelectItem>
                <SelectItem value="PKR" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">PKR (Rs)</SelectItem>
                <SelectItem value="THB" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">Thai (฿ THB)</SelectItem>
                <SelectItem value="IDR" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">Indonesian (Rp IDR)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Second row: Additional filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Engine Filter */}
            <Select value={filters.engine} onValueChange={(v) => handleEntityFilterChange('engine', v)}>
              <SelectTrigger><SelectValue placeholder={t("allEngines")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allEngines")}</SelectItem>
                {engines.map((e) => (
                  <SelectItem key={e.id} value={e.id} className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{e.name} ({e.code || "-"})</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Reseller Filter */}
            <Select value={filters.reseller} onValueChange={(v) => handleEntityFilterChange('reseller', v)}>
              <SelectTrigger><SelectValue placeholder={t("allResellers")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allResellers")}</SelectItem>
                {resellers.map((r) => (
                  <SelectItem key={r.id} value={r.id} className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{r.name} ({r.code || "-"})</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Merchant Filter */}
            <Select value={filters.merchant} onValueChange={(v) => handleEntityFilterChange('merchant', v)}>
              <SelectTrigger><SelectValue placeholder={t("allMerchants")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allMerchants")}</SelectItem>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{getMerchantName(m, language, t)} ({m.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Location Filter */}
            <Select value={filters.location} onValueChange={(v) => { setFilters({ ...filters, location: v }); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder={t("location")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allLocations")}</SelectItem>
                {locationOptions.map((location) => (
                  <SelectItem key={location.value} value={location.value} className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{location.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Provider Filter - using client_bank_name / bank_name */}
            <Select value={filters.provider} onValueChange={(v) => { setFilters({ ...filters, provider: v }); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder={t("allProviders")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allProviders")}</SelectItem>
                {providerOptions.map((provider) => (
                  <SelectItem key={provider.value} value={provider.value} className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{provider.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={filters.status} onValueChange={(v) => { setFilters({ ...filters, status: v }); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder={t("allStatus")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("allStatus")}</SelectItem>
                <SelectItem value="pending" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("pending")}</SelectItem>
                <SelectItem value="declined" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("declined") || "Declined"}</SelectItem>
                <SelectItem value="success" className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">{t("success")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Custom Date Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-start text-left font-normal !border-slate-200 hover:!border-slate-200 focus-visible:!border-slate-200 focus-visible:!ring-0 focus-visible:!ring-offset-0 hover:bg-transparent hover:text-current data-[state=open]:bg-transparent data-[state=open]:text-current data-[state=open]:!border-slate-200"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  <span className={customDateRangeLabel ? "text-slate-900" : "text-slate-700"}>
                    {customDateRangeLabel || t("customDateRange") || t("dateRange") || "Custom Date Range"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: filters.dateFrom || undefined, to: filters.dateTo || undefined }}
                  onSelect={(range) => {
                    setFilters({
                      ...filters,
                      dateFrom: range?.from || null,
                      dateTo: range?.to || null,
                    });
                    setPage(0);
                  }}
                  disabled={{ after: new Date() }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Transactions Table (deposits + withdrawals) */}
      <AdminTransactionTable
        transactions={combinedData}
        isLoading={isLoading}
        onView={setSelectedTx}
        showMerchant={true}
        showReseller={false}
        showEngine={false}
        merchants={merchants}
        resellers={resellers}
        engines={engines}
        isQRCode={false}
        mixedTypes={true}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, totalTransactions)} {t("of")} {totalTransactions.toLocaleString()} {t("transactions")}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        engine={selectedEngine}
        reseller={selectedReseller}
        merchant={selectedMerchant}
        isQRCode={selectedTx?.type !== "withdrawal"}
      />
    </div>
  );
}